import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { CanFlagCompanySpecified } from 'src/constants/permissionsList';
import useGuardedAsyncCallback from 'src/hooks/useGuardedAsyncCallback';
import usePermission from 'src/hooks/usePermissions';
import AddToFavoritesDropdown from 'src/sections/catalog_products/AddToFavoritesDropdown';
import { useContextSelector } from 'use-context-selector';
import ShoppingListItemFilterDropdown from './ShoppingListItemFilterDropdown';
import { ShoppingListProductsContext } from './ShoppingListProductsContext';

interface ShoppingListProductToolbarActionsProps {
  rightContent?: ReactNode;
  showItemFilter?: boolean;
}

const ShoppingListProductToolbarActions = ({
  rightContent,
  showItemFilter = true,
}: ShoppingListProductToolbarActionsProps) => {
  const { t } = useTranslation();
  const showCompanySpecified = usePermission({ permissions: CanFlagCompanySpecified });

  const hasSelectedItems = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.hasSelectedItems
  );
  const clearFilters = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.clearFilters
  );
  const hasActiveFilters = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.hasActiveFilters
  );
  const handleSelectedItemsAddToCart = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.handleSelectedItemsAddToCart
  );
  const isSupplierUser = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.isSupplierUser
  );
  const isSharedList = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.shoppingList?.isShare ?? false
  );
  const listOptions = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.listOptions
  );
  const handleMoveToList = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.handleMoveToList
  );
  const handleCopyToList = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.handleCopyToList
  );
  const handleDeleteSelected = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.handleDeleteSelected
  );
  const handleCompanySpecifiedAction = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.handleCompanySpecifiedAction
  );
  const isUpdatingCart = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.isUpdatingCart
  );
  const isMovingFavoriteProducts = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.isMovingFavoriteProducts
  );
  const isCopyingFavoriteProducts = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.isCopyingFavoriteProducts
  );
  const isDeletingFavoriteProducts = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.isDeletingFavoriteProducts
  );
  const isUpdatingCompanySpecified = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.isUpdatingCompanySpecified
  );

  const [handleAddToCartClick, isAddToCartPending] = useGuardedAsyncCallback(
    handleSelectedItemsAddToCart
  );

  const isCartActionPending = isUpdatingCart || isAddToCartPending;
  const isAnyActionPending =
    isCartActionPending ||
    isMovingFavoriteProducts ||
    isCopyingFavoriteProducts ||
    isDeletingFavoriteProducts ||
    isUpdatingCompanySpecified;
  const actionsDisabled = !hasSelectedItems;

  return (
    <div className="flex w-full flex-row flex-wrap items-center gap-x-2 gap-y-2">
      <div className="flex min-w-0 flex-row flex-wrap items-center gap-1">
        <ClearFilterButton onClick={clearFilters} disabled={!hasActiveFilters || isAnyActionPending} />
        {!isSupplierUser ? (
          <PopUp
            popupAlign={{ horizontal: 'center', vertical: 'top' }}
            anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
            className="max-h-[180px]"
            content={
              <div className="m-1 max-w-[350px]">
                <PopUpContentWrapper
                  type="Information"
                  contents={[t('Please select at least one item to Add to Cart')]}
                />
              </div>
            }
            show={!hasSelectedItems}
          >
            <Button
              color="success"
              size="lg"
              disabled={!hasSelectedItems || (isAnyActionPending && !isCartActionPending)}
              loading={isCartActionPending}
              onClick={handleAddToCartClick}
            >
              {t('Add to Cart')}
            </Button>
          </PopUp>
        ) : null}
        {!isSharedList ? (
          <AddToFavoritesDropdown
            label={t('Move to')}
            folders={listOptions}
            onAdd={handleMoveToList}
            disabled={actionsDisabled}
            isBusy={isAnyActionPending && !isMovingFavoriteProducts}
            disabledMessage={t('Please select at least one item to Move to')}
            isLoading={isMovingFavoriteProducts}
            size="lg"
          />
        ) : null}
        <AddToFavoritesDropdown
          label={t('Copy to')}
          folders={listOptions}
          onAddMultiple={handleCopyToList}
          multiSelect
          disabled={actionsDisabled}
          isBusy={isAnyActionPending && !isCopyingFavoriteProducts}
          disabledMessage={t('Please select at least one item to Copy to')}
          isLoading={isCopyingFavoriteProducts}
          size="lg"
        />
        {!isSharedList ? (
          <>
            {showCompanySpecified && (
              <AddToFavoritesDropdown
                label={t('Company Specified')}
                companySpecified
                onCompanySpecifiedAction={handleCompanySpecifiedAction}
                disabled={actionsDisabled}
                isBusy={isAnyActionPending && !isUpdatingCompanySpecified}
                disabledMessage={t('Please select at least one item to Company Specified')}
                isLoading={isUpdatingCompanySpecified}
                size="lg"
              />
            )}
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper
                    type="Information"
                    contents={[t('Please select at least one item to Delete')]}
                  />
                </div>
              }
              show={!hasSelectedItems}
            >
              <Button
                color="error"
                size="lg"
                disabled={actionsDisabled || (isAnyActionPending && !isDeletingFavoriteProducts)}
                loading={isDeletingFavoriteProducts}
                onClick={handleDeleteSelected}
              >
                {t('Delete')}
              </Button>
            </PopUp>
          </>
        ) : null}
      </div>
      <div className="ml-auto flex min-w-0 flex-row flex-wrap items-center justify-end gap-2">
        {rightContent}
        {showItemFilter && <ShoppingListItemFilterDropdown />}
      </div>
    </div>
  );
};

export default ShoppingListProductToolbarActions;
